<script setup>
/**
 * Monochrome vector icon set. Every icon is a 24×24 stroke drawing in
 * currentColor, so it takes the surrounding text color and theme tokens.
 * Decorative by default (aria-hidden); pass `label` to expose it to AT.
 * A `name` that is not a known icon and contains non-ASCII text (an emoji such as 🦞)
 * renders as text at the same size, so themes can pick an emoji as their icon.
 */
import { computed } from 'vue'

const props = defineProps({
  name: { type: String, required: true },
  size: { type: [Number, String], default: 20 },
  label: { type: String, default: '' },
  strokeWidth: { type: [Number, String], default: 2 }
})

// Stroke paths (no fills) unless noted. Keep drawings simple: they render at 16–96px.
const ICONS = {
  compass: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z M15.5 8.5l-2 5-5 2 2-5 5-2Z',
  pin: 'M12 21s-6-5.3-6-11a6 6 0 0 1 12 0c0 5.7-6 11-6 11Z M12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  ballot: 'M5 11h14v9a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-9Z M3 11h18 M8 11V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v6 M10 8h4',
  gift: 'M4 11h16v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-9Z M3 7h18v4H3z M12 7v14 M12 7c-2-3.5-6-3.5-6-1s4 1 6 1Z M12 7c2-3.5 6-3.5 6-1s-4 1-6 1Z',
  trophy: 'M8 4h8v5a4 4 0 0 1-8 0V4Z M8 6H5a3 3 0 0 0 3 4 M16 6h3a3 3 0 0 1-3 4 M12 13v3 M9 20h6 M10 16h4v4h-4z',
  ticket: 'M4 8a2 2 0 0 0 2-2h12a2 2 0 0 0 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 0-2 2H6a2 2 0 0 0-2-2v-2a2 2 0 0 0 0-4V8Z M10 6v12',
  medal: 'M12 21a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z M8.5 12 6 3h4l2 5 2-5h4l-2.5 9 M12 14.5l.9 1.8 2 .3-1.4 1.4.3 2-1.8-.9-1.8.9.3-2L9.1 16.6l2-.3.9-1.8Z',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  question: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .9-1 1.7 M12 17h.01',
  map: 'M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2V6Z M9 4v14 M15 6v14',
  hourglass: 'M7 3h10 M7 21h10 M8 3v3a4 4 0 0 0 2 3.5L12 11l2-1.5A4 4 0 0 0 16 6V3 M8 21v-3a4 4 0 0 1 2-3.5L12 13l2 1.5a4 4 0 0 1 2 3.5v3',
  party: 'M5 21l4-12 8 8-12 4Z M13 9c0-2 1-3 3-3s3-1 3-3 M15 13c2 0 3 1 4 3 M11 5c1-1 1-2 0-3 M18 12h.01 M20 8h.01 M8 6h.01',
  cone: 'M9 4h6l4 15H5L9 4Z M8 9h8 M6.5 14.5h11 M3 19h18',
  refresh: 'M20 12a8 8 0 1 1-2.3-5.7 M20 4v5h-5',
  users: 'M9 12a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z M3 20a6 6 0 0 1 12 0 M16 5.5a3 3 0 0 1 0 6 M21 20a5.5 5.5 0 0 0-4-5.3',
  chart: 'M4 20h16 M7 16v-5 M12 16V7 M17 16v-8',
  calendar: 'M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z M4 10h16 M8 3v4 M16 3v4',
  printer: 'M7 8V4h10v4 M5 8h14a1 1 0 0 1 1 1v7h-4v3H8v-3H4V9a1 1 0 0 1 1-1Z M8 14h8',
  lock: 'M6 11h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z M8 11V8a4 4 0 1 1 8 0v3 M12 15v2',
  inbox: 'M4 13l2.5-8h11L20 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-6Z M4 13h5l1 2h4l1-2h5',
  signal: 'M2 9.5a15 15 0 0 1 20 0 M5.5 13a10 10 0 0 1 13 0 M9 16.5a5 5 0 0 1 6 0 M12 20h.01',
  wave: 'M8 12V6.5a1.5 1.5 0 1 1 3 0V11 M11 10V4.5a1.5 1.5 0 1 1 3 0V11 M14 10.5V6a1.5 1.5 0 1 1 3 0v6.5 M17 10a1.5 1.5 0 1 1 3 0v3c0 4.4-3.1 8-7.5 8S5 17.4 5 13v-2.5a1.5 1.5 0 1 1 3 0V13',
  'arrow-up': 'M12 19V5 M6 11l6-6 6 6',
  'arrow-right': 'M5 12h14 M13 6l6 6-6 6',
  'arrow-down': 'M12 5v14 M6 13l6 6 6-6',
  'chevron-left': 'M15 6l-6 6 6 6',
  'chevron-right': 'M9 6l6 6-6 6',
  close: 'M6 6l12 12 M18 6 6 18',
  home: 'M4 11l8-7 8 7 M6 10v10h12V10 M10 20v-6h4v6',
  flag: 'M5 21V4 M5 4h12l-2 4 2 4H5',
  dice: 'M5 5h14v14H5z M9 9h.01 M15 9h.01 M12 12h.01 M9 15h.01 M15 15h.01',
  phone: 'M8 3h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z M11 18h2',
  external: 'M14 4h6v6 M20 4l-9 9 M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  menu: 'M4 7h16 M4 12h16 M4 17h16',
  sparkle: 'M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z M19 17l.7 1.8 1.8.7-1.8.7L19 22l-.7-1.8-1.8-.7 1.8-.7L19 17Z',
  account: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z M4 21a8 8 0 0 1 16 0',
  book: 'M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2V5Z M4 19a2 2 0 0 0 2 2h13 M8 7h7 M8 11h5',
  tree: 'M12 3l5 7h-3l4 5h-4l3 5H7l3-5H6l4-5H7l5-7Z M12 20v2',
  send: 'M21 3 3 10.5l7.5 3L13.5 21 21 3Z M10.5 13.5 21 3',
  mail: 'M4 6h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z M3 7l9 6 9-6',
  key: 'M14.5 13.5a4.5 4.5 0 1 0-4.2-2.9L3 18v3h3l1.5-1.5v-2h2v-2h2l1.9-1.9a4.5 4.5 0 0 0 1.1.9Z M16 8h.01',
  eye: 'M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  upload: 'M12 16V4 M7 9l5-5 5 5 M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3',
  'arrow-left': 'M19 12H5 M11 6l-6 6 6 6',
  megaphone: 'M3 11v2a1 1 0 0 0 1 1h2l8 4V6L6 10H4a1 1 0 0 0-1 1Z M6 14l1.5 6H10l-1-6 M17 9.5a3 3 0 0 1 0 5 M19.5 7a7 7 0 0 1 0 10',
  gear: 'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2Z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z'
}

const emoji = computed(() => (!ICONS[props.name] && /[^ -~]/.test(props.name)) ? props.name : '')
const d = computed(() => ICONS[props.name] || ICONS.question)
</script>

<template>
  <span
    v-if="emoji" class="inline-block shrink-0 leading-none align-[-0.15em]" :style="{ fontSize: size + 'px' }"
    :aria-hidden="label ? undefined : 'true'" :role="label ? 'img' : undefined" :aria-label="label || undefined"
  >{{ emoji }}</span>
  <svg
    v-else
    :width="size" :height="size" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    :stroke-width="strokeWidth" stroke-linecap="round" stroke-linejoin="round"
    class="inline-block shrink-0 align-[-0.15em]"
    :aria-hidden="label ? undefined : 'true'" :role="label ? 'img' : undefined" :aria-label="label || undefined"
  >
    <path :d="d" />
  </svg>
</template>
